import { BRAND_LOGOS } from '@/app/ui/menus/AgentConnectionSetup/brandLogoData';
import type { AgentCatalogTile } from 'quadratic-shared/typesAndSchemasAgentConnections';
import { memo, useEffect, useState } from 'react';

/**
 * Initial-badge fallback for `BrandLogo` when neither a thumbnail URL nor
 * an iconify/simple-icons entry is available. Renders the first letter of
 * the service name inside a circular badge tinted with the brand color.
 */
const AppBrandIcon = memo(
  ({ name, color, size = 24, className }: { name: string; color: string; size?: number; className?: string }) => {
    const letter = (name?.trim()?.[0] ?? '?').toUpperCase();
    return (
      <span
        className={className}
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          width: size,
          height: size,
          flexShrink: 0,
          borderRadius: '50%',
          background: color || '#888',
          color: '#fff',
          fontWeight: 600,
          fontSize: Math.floor(size * 0.55),
          lineHeight: 1,
        }}
        aria-label={name}
      >
        {letter}
      </span>
    );
  }
);
AppBrandIcon.displayName = 'AppBrandIcon';

interface BrandLogoProps {
  slug: string;
  name: string;
  /** Brand color used for simple-icons tinting and the initial-badge fallback. */
  color: string;
  /**
   * Admin-managed thumbnail URL from the catalog entry. Rendered as the
   * primary image; falls back to the iconify/simple-icons cascade below if
   * the URL fails to load (404, CORS, etc.).
   */
  thumbnailUrl?: string | null;
  size?: number;
  className?: string;
}

/**
 * Resolve the thumbnail URL for an AgentConnection row. Custom (off-catalog)
 * connections persist the AI-found URL on the row itself; catalog-backed
 * connections leave that field null and inherit the centrally-managed
 * thumbnail from the catalog tile. Used everywhere a connection's BrandLogo
 * is rendered so the setup-panel header and the various connection lists
 * agree on the same logo.
 */
export const resolveConnectionThumbnail = (
  connection: { thumbnailUrl?: string | null },
  tile: Pick<AgentCatalogTile, 'thumbnailUrl'> | null | undefined
): string | null => connection.thumbnailUrl ?? tile?.thumbnailUrl ?? null;

/**
 * Renders a brand logo. Prefers:
 *   1. Admin-supplied `thumbnailUrl` (from the catalog entry)
 *   2. `@iconify-json/logos` full-color SVG (when available)
 *   3. `simple-icons` monochrome path tinted with the brand color
 *   4. Initial-badge fallback (AppBrandIcon)
 *
 * The underlying icon data is pre-extracted into `brandLogoData.ts` at build
 * time so we don't ship either icon library at runtime.
 */
export const BrandLogo = memo(({ slug, name, color, thumbnailUrl, size = 24, className }: BrandLogoProps) => {
  const [thumbnailErrored, setThumbnailErrored] = useState(false);
  // Reset the error state when the URL changes so a fresh URL gets a
  // chance to load even after a previous one 404'd.
  useEffect(() => {
    setThumbnailErrored(false);
  }, [thumbnailUrl]);

  if (thumbnailUrl && !thumbnailErrored) {
    return (
      <span
        className={className}
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          width: size,
          height: size,
          flexShrink: 0,
        }}
        aria-label={name}
      >
        <img
          src={thumbnailUrl}
          alt=""
          width={size}
          height={size}
          style={{ width: size, height: size, objectFit: 'contain' }}
          onError={() => setThumbnailErrored(true)}
        />
      </span>
    );
  }

  const data = BRAND_LOGOS[slug];

  if (!data) {
    return <AppBrandIcon name={name} color={color} size={size} className={className} />;
  }

  if (data.source === 'logos') {
    // Full-color brand mark — render the body verbatim inside an SVG of the
    // requested size. Wrap in a rounded container so logos with transparent
    // backgrounds still look tidy against any surface.
    return (
      <span
        className={className}
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          width: size,
          height: size,
          flexShrink: 0,
        }}
        aria-label={name}
      >
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width={size}
          height={size}
          viewBox={`0 0 ${data.width} ${data.height}`}
          dangerouslySetInnerHTML={{ __html: data.body }}
        />
      </span>
    );
  }

  // simple-icons: tinted monochrome.
  return (
    <span
      className={className}
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        width: size,
        height: size,
        flexShrink: 0,
      }}
      aria-label={name}
    >
      <svg xmlns="http://www.w3.org/2000/svg" width={size} height={size} viewBox="0 0 24 24" fill={color}>
        <path d={data.path} />
      </svg>
    </span>
  );
});

BrandLogo.displayName = 'BrandLogo';
